import type { ApiEnvelope } from '@/types/api';
import type {
  ChangePasswordInput,
  ForgotPasswordInput,
  LoginInput,
  LoginResponse,
  OtpRequiredResponse,
  PortalUser,
  ResetPasswordInput,
  VerifyOtpInput,
} from '@/types/auth';

import { api } from './client';
import { endpoints } from './endpoints';

/**
 * Auth calls. Each unwraps the envelope and returns the useful payload, so
 * callers never handle `{ code, status, data, message }` themselves.
 */

/**
 * Step 1 of sign-in: the password.
 *
 * Returns an `OtpRequiredResponse` in practice — every portal account requires
 * an emailed code — but the union is honest about the contract rather than the
 * current configuration, so turning MFA off server-side would not silently make
 * this type a lie. Discriminate with `isOtpRequired`.
 */
export async function login(input: LoginInput): Promise<LoginResponse | OtpRequiredResponse> {
  const { data } = await api.post<ApiEnvelope<LoginResponse | OtpRequiredResponse>>(
    endpoints.login,
    input,
  );
  return data.data;
}

/**
 * Step 2: the emailed code. This is where the token actually comes from.
 *
 * A 401 here is "wrong code", never "session expired" — see
 * CREDENTIAL_ENDPOINTS. When the rejection also carries
 * `data.reason = 'otp_challenge_expired'` the challenge itself is dead and the
 * user has to start again at sign-in; `isOtpChallengeExpired` reads that.
 */
export async function verifyOtp(input: VerifyOtpInput): Promise<LoginResponse> {
  const { data } = await api.post<ApiEnvelope<LoginResponse>>(endpoints.verifyOtp, input);
  return data.data;
}

/**
 * Emails a replacement code for a sign-in already under way.
 *
 * The challenge is unchanged — the client no longer holds the password and
 * could not start over — so only the code and its expiry are refreshed. Returns
 * the server's message for display.
 */
export async function resendOtp(challenge: string): Promise<string> {
  const { data } = await api.post<ApiEnvelope<OtpRequiredResponse>>(endpoints.resendOtp, {
    challenge,
  });
  return data.message;
}

export async function fetchMe(): Promise<PortalUser> {
  const { data } = await api.get<ApiEnvelope<{ user: PortalUser }>>(endpoints.me);
  return data.data.user;
}

export async function logout(): Promise<void> {
  await api.post(endpoints.logout);
}

/**
 * Always resolves for a well-formed request, whether or not the address has an
 * account — the backend deliberately returns an identical body either way so
 * the endpoint can't be used to discover which emails are registered.
 */
export async function forgotPassword(input: ForgotPasswordInput): Promise<string> {
  const { data } = await api.post<ApiEnvelope<unknown>>(endpoints.forgotPassword, input);
  return data.message;
}

export async function resetPassword(input: ResetPasswordInput): Promise<string> {
  const { data } = await api.post<ApiEnvelope<unknown>>(endpoints.resetPassword, input);
  return data.message;
}

/**
 * Replaces the generated password an account was provisioned with.
 *
 * Returns the refreshed user, whose `must_change_password` is now false, so the
 * caller can seed the cache without a second round trip.
 */
export async function changePassword(input: ChangePasswordInput): Promise<PortalUser> {
  const { data } = await api.post<ApiEnvelope<{ user: PortalUser }>>(
    endpoints.changePassword,
    input,
  );
  return data.data.user;
}
